/**
 * Knowledge Base Data Structure and Management
 * Handles all knowledge base articles for music industry best practices
 */

// Master knowledge base articles based on music industry requirements
export const KNOWLEDGE_BASE_ARTICLES = {
  SPOTIFY_EDITORIAL_PITCH: {
    id: 'spotify_editorial_pitch',
    title: 'Submitting a Song via Spotify\'s Editorial Pitch Tool',
    category: 'Distribution & DSPs',
    summary: 'How to pitch unreleased songs for editorial playlist consideration through Spotify for Artists',
    content: `**What it is:**

Spotify allows you to pitch unreleased songs for editorial playlist consideration inside your Spotify for Artists dashboard.

**How to do it:**

1. Distribute your song at least 3–4 weeks before release.
2. Log into Spotify for Artists: https://artists.spotify.com/home
3. Go to the Music > Upcoming tab.
4. Select the song you want to pitch.
5. Fill out the submission form (genre, mood, instruments, languages, location).
6. Submit at least 7 days before release (ideally 14+ days).

**Tips:**

• Once you submit your song to DSP's for release, add it as an upcoming release in the catalog app and dizee will automatically remind you about things like pitch deadlines, pre-saves, notable playlist adds and more.

• Only one track per release can be pitched. Choose your lead wisely.

• Info to prepare: In the pitch, include what you have planned to market the release, any budgets, special moments, live play and/or pre-release hype and support around the song.`,
    tags: ['spotify', 'editorial', 'playlists', 'pitching', 'dsp'],
    difficulty: 'Beginner',
    timeToComplete: '15-30 minutes',
    lastUpdated: '2024-01-15'
  },

  PRO_REGISTRATION: {
    id: 'pro_registration',
    title: 'Registering a Song with ASCAP, BMI, SEASAC or SOCAN',
    category: 'Publishing & Royalties',
    summary: 'Step-by-step guide to register songs with Performance Rights Organizations',
    content: `**What it is:**

To earn publishing royalties in the U.S., your songs must be registered with a PRO like ASCAP. If you have a publisher they may register them for you depending on your deal but if not here is a step by step process to do it yourself.

**How to do it:**

1. Log into ASCAP Member Access, BMI, SESAC OR SOCAN
   - SESAC does not accept unsolicited membership applications. Instead, the artist representative, like a lawyer, manager, or agent, must reach out on your behalf to spark interest. You cannot simply apply online as an invitation and approval process is required.

2. Go to Works Registration.
3. Click "Register a New Work."
4. Fill in Info: Title, writers, publishers, ISRC (if available), % splits.
5. Click Submit.

**Pro Tip:**

• Download your PRO statement and drop a screenshot of it into the dizee AI to cross reference with your dizee catalog entries to see if you have every song registered and/or flag any discrepancies.

• All co-writers must register the same song title and splits with their respective PROs to receive royalties

**Info to prepare:** Label copy such as writer and producer credits, splits, publishing lines, PRO and ISRC.`,
    tags: ['ascap', 'bmi', 'sesac', 'socan', 'pro', 'publishing', 'royalties'],
    difficulty: 'Intermediate',
    timeToComplete: '30-45 minutes',
    lastUpdated: '2024-01-15'
  },

  SOUNDEXCHANGE_REGISTRATION: {
    id: 'soundexchange_registration',
    title: 'Registering with SoundExchange',
    category: 'Publishing & Royalties',
    summary: 'How to collect digital performance royalties from non-interactive streams',
    content: `**What it is:**

SoundExchange collects royalties from non-interactive digital streams (e.g. Pandora, SiriusXM).

**How to do it:**

1. Go to soundexchange.com.
2. Click "Register Now" → choose "Sound Recording Owner" and/or "Featured Artist."
3. Fill in artist info, tax info, and banking details.
4. Upload catalog information or register songs manually.

**Pro Tip:**

This is separate from your distributor and PRO, many artists are missing this revenue.`,
    tags: ['soundexchange', 'digital', 'royalties', 'pandora', 'siriusxm'],
    difficulty: 'Beginner',
    timeToComplete: '20-30 minutes',
    lastUpdated: '2024-01-15'
  },

  PUBLISHING_ADMIN: {
    id: 'publishing_admin',
    title: 'Registering Your Catalog with a Publishing Admin',
    category: 'Publishing & Royalties',
    summary: 'Using publishing administrators to collect global royalties',
    content: `**What it is:**

Admin publishers collect global royalties not covered by your U.S. or CANADIAN PRO (like mechanicals & international income). Usually you only need this if you don't already have a publisher who would act as the admin.

**How to do it:**

1. Sign up at Songtrust or Sentric.
2. Import your catalog (ISRCs, titles, writer info).
3. Match splits with co-writers.
4. Let them collect globally (they'll take ~15% cut).

**Pro Tip:**

If you don't use an admin, you're leaving 30–50% of your publishing income on the table.`,
    tags: ['songtrust', 'sentric', 'publishing', 'admin', 'global', 'mechanicals'],
    difficulty: 'Intermediate',
    timeToComplete: '45-60 minutes',
    lastUpdated: '2024-01-15'
  },

  LLC_SETUP: {
    id: 'llc_setup',
    title: 'Setting Up an LLC for Your Artist Project',
    category: 'Business & Legal',
    summary: 'Legal business structure to protect personal assets and manage music finances',
    content: `**What it is:**

Forming an LLC separates your music finances and protects your personal assets. Search for the trademark name search database in your state.

**How to do it:**

1. Choose a name (check name availability in your state).
2. File with your state's Secretary of State website.
3. Get an EIN at irs.gov.
4. Open a business bank account.

**Pro Tip:**

• Use services like LegalZoom or ZenBusiness if you want help — or let Dizee track LLC docs & tax info.

• It's good to also check your state's trademark database for the name in the categories related to recorded music, live performances and merch.`,
    tags: ['llc', 'business', 'legal', 'trademark', 'irs', 'ein'],
    difficulty: 'Advanced',
    timeToComplete: '1-2 hours',
    lastUpdated: '2024-01-15'
  },

  TOUR_DATES_UPLOAD: {
    id: 'tour_dates_upload',
    title: 'Uploading Tour Dates to Bandsintown and Songkick',
    category: 'Live Shows & Touring',
    summary: 'Sync tour dates across platforms and DSPs automatically',
    content: `**What it is:**

These platforms push your shows to fans and automatically display tour dates on Spotify and other DSPs.

**How to do it:**

1. Go to bandsintown.com or tourbox.songkick.com.
2. Log in and claim your artist profile.
3. Add tour dates manually or import from your booking calendar.
4. Link your accounts to Spotify, Instagram, and Facebook.

**Pro Tip:**

Enable automatic updates so your DSP profiles stay in sync without manual entry.`,
    tags: ['bandsintown', 'songkick', 'tour', 'live', 'shows', 'spotify'],
    difficulty: 'Beginner',
    timeToComplete: '15-25 minutes',
    lastUpdated: '2024-01-15'
  },

  RETARGETING_PIXELS: {
    id: 'retargeting_pixels',
    title: 'Setting Up Retargeting Pixels in Your Smart Link',
    category: 'Marketing & Promotion',
    summary: 'Track users for retargeting ads to lower advertising costs',
    content: `**What it is:**

Pixels track users who clicked your link so you can run ads to them later.

**How to do it:**

1. Go to your smart link provider (ToneDen, Feature.fm).
2. Go to your campaign > Settings > Pixel Tracking.
3. Paste in your Facebook Pixel ID or TikTok Pixel ID.
4. Save and publish your link.
5. Use the pixel to create custom audiences in your ad platform.

**Pro Tip:**

Retargeting fans who clicked your link can lower your ad costs dramatically when you boost a new release.`,
    tags: ['retargeting', 'pixels', 'ads', 'facebook', 'tiktok', 'smartlink'],
    difficulty: 'Intermediate',
    timeToComplete: '20-30 minutes',
    lastUpdated: '2024-01-15'
  },

  SHOPIFY_SOCIAL_INTEGRATION: {
    id: 'shopify_social_integration',
    title: 'Connecting Your Shopify Store to Instagram & TikTok',
    category: 'Marketing & Promotion',
    summary: 'Enable product tagging in social media content',
    content: `**What it is:**

This enables product tagging in Reels, Stories, and videos.

**How to do it:**

1. In Shopify, go to Sales Channels > Add Instagram or TikTok.
2. Connect your Meta/TikTok business account.
3. Verify your domain in Meta Business Manager or TikTok Business Center.
4. Enable product tagging.

**Pro Tip:**

Product tags can boost conversion rates 2–3x vs. linking in bio only.`,
    tags: ['shopify', 'instagram', 'tiktok', 'ecommerce', 'product-tags'],
    difficulty: 'Intermediate',
    timeToComplete: '30-45 minutes',
    lastUpdated: '2024-01-15'
  },

  DIZEE_TOUR_IMPORT: {
    id: 'dizee_tour_import',
    title: 'Importing Tour Dates into Dizee from a CSV File',
    category: 'Live Shows & Touring',
    summary: 'Bulk upload tour dates using CSV for efficient management',
    content: `**What it is:**

Bulk upload saves time when entering many tour dates at once.

**How to do it:**

1. Export your tour calendar as a .CSV from your booking agency or using a dizee spreadsheet template.
2. In Dizee's Bookings app, click "Import Dates."
3. Upload the file and match columns (Date, Venue, City, Support, etc.).
4. Review and confirm.

**Pro Tip:**

Use the template provided by Dizee for perfect column formatting.`,
    tags: ['dizee', 'bookings', 'csv', 'import', 'tour', 'bulk-upload'],
    difficulty: 'Beginner',
    timeToComplete: '10-15 minutes',
    lastUpdated: '2024-01-15'
  },

  PRE_SAVE_CAMPAIGN: {
    id: 'pre_save_campaign',
    title: 'Creating a Pre-Save Campaign for Your Song',
    category: 'Marketing & Promotion',
    summary: 'Boost first-day streams and Release Radar placement with pre-saves',
    content: `**What it is:**

Pre-saves allow fans to save your song before it drops, boosting first-day streams and Release Radar placement.

**How to do it:**

1. Distribute your release through your distributor or label with a release date 3–4 weeks in advance. Ask them if they will provide you with a link and if not you can setup your own.
2. Go to Feature.fm, Linkfire, or Hypeddit.
3. Choose "Pre-Save" as the campaign type.
4. Paste your Spotify track or release link (or UPC).
5. Customize the visuals and URL.
6. Share the link across your socials and email list.

**Pro Tip:**

Use the pre-save page to collect emails and create retargeting pixels for ads later.`,
    tags: ['pre-save', 'campaign', 'spotify', 'feature.fm', 'linkfire', 'hypeddit'],
    difficulty: 'Beginner',
    timeToComplete: '20-30 minutes',
    lastUpdated: '2024-01-15'
  }
};

// Knowledge Base Categories
export const KNOWLEDGE_BASE_CATEGORIES = {
  'Distribution & DSPs': {
    id: 'distribution-dsps',
    name: 'Distribution & DSPs',
    color: '#1DB954',
    description: 'Distribution platforms and digital service providers'
  },
  'Publishing & Royalties': {
    id: 'publishing-royalties',
    name: 'Publishing & Royalties',
    color: '#34C759',
    description: 'Rights management and revenue collection'
  },
  'Business & Legal': {
    id: 'business-legal',
    name: 'Business & Legal',
    color: '#FF9500',
    description: 'Legal structures and business setup'
  },
  'Live Shows & Touring': {
    id: 'live-touring',
    name: 'Live Shows & Touring',
    color: '#FFFF00',
    description: 'Live performance and tour management'
  },
  'Marketing & Promotion': {
    id: 'marketing-promotion',
    name: 'Marketing & Promotion',
    color: '#5856D6',
    description: 'Promotional strategies and fan engagement'
  }
};

// Knowledge Base instance structure
export class KnowledgeBaseArticle {
  constructor(articleData) {
    this.id = articleData.id;
    this.title = articleData.title;
    this.category = articleData.category;
    this.summary = articleData.summary;
    this.content = articleData.content;
    this.tags = articleData.tags || [];
    this.difficulty = articleData.difficulty || 'Beginner';
    this.timeToComplete = articleData.timeToComplete || 'Unknown';
    this.lastUpdated = articleData.lastUpdated || new Date().toISOString().split('T')[0];
    this.viewed = false;
    this.bookmarked = false;
    this.viewedAt = null;
    this.viewCount = 0;
  }

  markViewed() {
    this.viewed = true;
    this.viewedAt = new Date().toISOString();
    this.viewCount += 1;
  }

  toggleBookmark() {
    this.bookmarked = !this.bookmarked;
  }

  getWordCount() {
    return this.content.split(' ').length;
  }

  getReadingTime() {
    const wordsPerMinute = 200;
    const words = this.getWordCount();
    return Math.ceil(words / wordsPerMinute);
  }
}

// Knowledge Base Manager Class
export class KnowledgeBaseManager {
  constructor() {
    this.articles = this.loadArticles();
    this.favorites = this.loadFavorites();
    this.searchHistory = this.loadSearchHistory();
  }

  // Load articles from templates and localStorage
  loadArticles() {
    try {
      const stored = localStorage.getItem('dizee_knowledge_base');
      let storedData = {};
      
      if (stored) {
        storedData = JSON.parse(stored);
      }

      // Create articles from templates and merge with stored data
      const articles = Object.values(KNOWLEDGE_BASE_ARTICLES).map(articleData => {
        const article = new KnowledgeBaseArticle(articleData);
        
        // Merge with stored data if available
        const storedArticle = storedData[article.id];
        if (storedArticle) {
          article.viewed = storedArticle.viewed || false;
          article.bookmarked = storedArticle.bookmarked || false;
          article.viewedAt = storedArticle.viewedAt || null;
          article.viewCount = storedArticle.viewCount || 0;
        }
        
        return article;
      });

      return articles;
    } catch (error) {
      console.error('Error loading knowledge base articles:', error);
      return Object.values(KNOWLEDGE_BASE_ARTICLES).map(articleData => new KnowledgeBaseArticle(articleData));
    }
  }

  // Save articles to localStorage
  saveArticles() {
    try {
      const dataToStore = {};
      this.articles.forEach(article => {
        dataToStore[article.id] = {
          viewed: article.viewed,
          bookmarked: article.bookmarked,
          viewedAt: article.viewedAt,
          viewCount: article.viewCount
        };
      });
      localStorage.setItem('dizee_knowledge_base', JSON.stringify(dataToStore));
    } catch (error) {
      console.error('Error saving knowledge base articles:', error);
    }
  }

  // Load favorites from localStorage
  loadFavorites() {
    try {
      const stored = localStorage.getItem('dizee_kb_favorites');
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Error loading favorites:', error);
      return [];
    }
  }

  // Save favorites to localStorage
  saveFavorites() {
    try {
      localStorage.setItem('dizee_kb_favorites', JSON.stringify(this.favorites));
    } catch (error) {
      console.error('Error saving favorites:', error);
    }
  }

  // Load search history from localStorage
  loadSearchHistory() {
    try {
      const stored = localStorage.getItem('dizee_kb_search_history');
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Error loading search history:', error);
      return [];
    }
  }

  // Save search history to localStorage
  saveSearchHistory() {
    try {
      localStorage.setItem('dizee_kb_search_history', JSON.stringify(this.searchHistory));
    } catch (error) {
      console.error('Error saving search history:', error);
    }
  }

  // Get all articles
  getAllArticles() {
    return this.articles;
  }

  // Get article by ID
  getArticleById(id) {
    return this.articles.find(article => article.id === id);
  }

  // Get articles by category
  getArticlesByCategory(category) {
    return this.articles.filter(article => article.category === category);
  }

  // Get bookmarked articles
  getBookmarkedArticles() {
    return this.articles.filter(article => article.bookmarked);
  }

  // Get recently viewed articles
  getRecentlyViewedArticles(limit = 5) {
    return this.articles
      .filter(article => article.viewed)
      .sort((a, b) => new Date(b.viewedAt) - new Date(a.viewedAt))
      .slice(0, limit);
  }

  // Search articles
  searchArticles(query, filters = {}) {
    if (!query || query.trim() === '') {
      return this.getAllArticles();
    }

    const searchQuery = query.toLowerCase().trim();
    
    // Add to search history
    if (!this.searchHistory.includes(searchQuery)) {
      this.searchHistory.unshift(searchQuery);
      this.searchHistory = this.searchHistory.slice(0, 10); // Keep last 10 searches
      this.saveSearchHistory();
    }

    let results = this.articles.filter(article => {
      const titleMatch = article.title.toLowerCase().includes(searchQuery);
      const summaryMatch = article.summary.toLowerCase().includes(searchQuery);
      const contentMatch = article.content.toLowerCase().includes(searchQuery);
      const tagsMatch = article.tags.some(tag => tag.toLowerCase().includes(searchQuery));
      const categoryMatch = article.category.toLowerCase().includes(searchQuery);

      return titleMatch || summaryMatch || contentMatch || tagsMatch || categoryMatch;
    });

    // Apply filters
    if (filters.category) {
      results = results.filter(article => article.category === filters.category);
    }

    if (filters.difficulty) {
      results = results.filter(article => article.difficulty === filters.difficulty);
    }

    if (filters.bookmarked) {
      results = results.filter(article => article.bookmarked);
    }

    return results;
  }

  // Mark article as viewed
  markArticleViewed(articleId) {
    const article = this.getArticleById(articleId);
    if (article) {
      article.markViewed();
      this.saveArticles();
      return true;
    }
    return false;
  }

  // Toggle article bookmark
  toggleArticleBookmark(articleId) {
    const article = this.getArticleById(articleId);
    if (article) {
      article.toggleBookmark();
      this.saveArticles();
      return article.bookmarked;
    }
    return false;
  }

  // Get categories with article counts
  getCategoriesWithCounts() {
    const categoryCounts = {};
    
    Object.values(KNOWLEDGE_BASE_CATEGORIES).forEach(category => {
      categoryCounts[category.name] = {
        ...category,
        count: this.getArticlesByCategory(category.name).length
      };
    });

    return categoryCounts;
  }

  // Get popular tags
  getPopularTags(limit = 10) {
    const tagCounts = {};
    
    this.articles.forEach(article => {
      article.tags.forEach(tag => {
        tagCounts[tag] = (tagCounts[tag] || 0) + 1;
      });
    });

    return Object.entries(tagCounts)
      .sort(([,a], [,b]) => b - a)
      .slice(0, limit)
      .map(([tag, count]) => ({ tag, count }));
  }

  // Generate AI context for knowledge base
  generateAIContext() {
    const total = this.articles.length;
    const viewed = this.articles.filter(a => a.viewed).length;
    const bookmarked = this.getBookmarkedArticles().length;
    const categories = this.getCategoriesWithCounts();

    return {
      total_articles: total,
      viewed_articles: viewed,
      bookmarked_articles: bookmarked,
      categories: categories,
      popular_tags: this.getPopularTags(),
      recent_searches: this.searchHistory.slice(0, 5),
      recently_viewed: this.getRecentlyViewedArticles().map(a => ({
        id: a.id,
        title: a.title,
        category: a.category,
        viewedAt: a.viewedAt
      }))
    };
  }
}

// Global instance
export const knowledgeBaseManager = new KnowledgeBaseManager();

// Export convenience functions
export const getAllArticles = () => knowledgeBaseManager.getAllArticles();
export const getArticleById = (id) => knowledgeBaseManager.getArticleById(id);
export const getArticlesByCategory = (category) => knowledgeBaseManager.getArticlesByCategory(category);
export const getBookmarkedArticles = () => knowledgeBaseManager.getBookmarkedArticles();
export const getRecentlyViewedArticles = (limit) => knowledgeBaseManager.getRecentlyViewedArticles(limit);
export const searchArticles = (query, filters) => knowledgeBaseManager.searchArticles(query, filters);
export const markArticleViewed = (id) => knowledgeBaseManager.markArticleViewed(id);
export const toggleArticleBookmark = (id) => knowledgeBaseManager.toggleArticleBookmark(id);
export const getCategoriesWithCounts = () => knowledgeBaseManager.getCategoriesWithCounts();
export const getPopularTags = (limit) => knowledgeBaseManager.getPopularTags(limit); 